import React, { Fragment, useState, useEffect } from "react";
import { useMeeting } from "../../config/sdkConfig";
import useIsMobile from "../../hooks/useIsMobile";
import useIsTab from "../../hooks/useIsTab";
import useWindowSize from "../../hooks/useWindowSize";
import { XMarkIcon, BellIcon } from "@heroicons/react/24/outline";
import { AgoraRTMChatPanel } from "./AgoraRTMChatPanel";
import { ParticipantPanel } from "./ParticipantPanel";
import { WebinarParticipantPanel } from "./WebinarParticipantPanel";
import { PrompterPanel } from "./PrompterPanel";
import AIChatAssistant from "./AIChatAssistant";
import ChatSummaryPanel from "./ChatSummaryPanel";
import NotificationSettingsModal from "./NotificationSettingsModal";
import { Dialog, Transition } from "@headlessui/react";
import { useMediaQuery } from "react-responsive";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useSiteContext } from "../../context/SiteContext";

// Wrapper component for Agora RTM Chat - uses parent's RTM context
function AgoraRTMChatWrapper({ panelHeight }) {
  const mMeeting = useMeeting();
  const meetingId = mMeeting?.meetingId;
  const localParticipant = mMeeting?.localParticipant;
  const userId = localParticipant?.id;

  if (!meetingId || !userId) {
    return (
      <div
        className="flex items-center justify-center text-white bg-black"
        style={{ height: panelHeight }}
      >
        <p className="text-sm text-gray-400">Waiting for meeting connection...</p>
      </div>
    );
  }

  // RTM context is provided by parent (Conference.js -> AgoraRTMProvider)
  return <AgoraRTMChatPanel panelHeight={panelHeight} />;
}

const SideBarTabView = ({
  height,
  sideBarContainerWidth,
  panelHeight,
  panelHeaderHeight,
  panelHeaderPadding,
  panelPadding,
  handleClose,
}) => {
  const { participants } = useMeeting();
  const { sideBarMode, participantApproveFunc, setParticipantApproveFunc } =
    useMeetingAppContext();
  const { meetingSettings } = useSiteContext();

  // State for AI Assistant, Summary, and Notifications
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    newMessages: true,
    privateOnly: false,
    mentions: true,
    urgentQueries: true,
    sound: true,
    desktop: true,
  });

  return (
    <div
      className={`bg-[#001233] ${
        participantApproveFunc && participantApproveFunc.length > 0
          ? "z-[999]"
          : ""
      }`}
      style={{
        height,
        width: sideBarContainerWidth,
        paddingTop: panelPadding,
        paddingLeft: panelPadding,
        paddingRight: panelPadding,
        paddingBottom: panelPadding,
      }}
    >
      <div>
        <div
          className="bg-[#0a1628]"
          style={{
            height: height,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <>
            {sideBarMode && (
              <div
                className={`flex items-center justify-between`}
                style={{
                  padding: panelHeaderPadding,
                  height: panelHeaderHeight - 1,
                  borderBottom: "1px solid #70707033",
                }}
              >
                <p className="text-base text-white font-bold">
                  {sideBarMode === "PARTICIPANTS"
                    ? `${
                        sideBarMode.charAt(0).toUpperCase() +
                          sideBarMode.slice(1).toLowerCase() || ""
                      } (${new Map(participants)?.size})`
                    : sideBarMode.charAt(0).toUpperCase() +
                        sideBarMode.slice(1).toLowerCase() || ""}
                </p>
                <div className="flex items-center gap-2">
                  {sideBarMode === "CHAT" && (
                    <>
                      {/* AI Assistant Button */}
                      <button
                        onClick={() => setShowAIAssistant(true)}
                        className="flex items-center gap-1.5 px-2 py-1 hover:bg-white/10 rounded-lg transition-colors text-xs text-gray-400 hover:text-white"
                        title="AI Chat Assistant"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="hidden sm:inline">AI Assistant</span>
                      </button>

                      {/* Summary Button */}
                      <button
                        onClick={() => setShowSummary(!showSummary)}
                        className={`flex items-center gap-1.5 px-2 py-1 hover:bg-white/10 rounded-lg transition-colors text-xs ${
                          showSummary ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white'
                        }`}
                        title="Chat Summary"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="hidden sm:inline">Summary</span>
                      </button>

                      {/* Notification Settings Button */}
                      <button
                        onClick={() => setShowNotificationSettings(true)}
                        className="p-1.5 hover:bg-white/10 rounded-lg transition-colors relative"
                        title="Notification Settings"
                      >
                        <BellIcon className="w-4 h-4 text-gray-400" />
                        {notificationSettings.newMessages && (
                          <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-purple-500 rounded-full"></span>
                        )}
                      </button>

                      {/* Export Button - keeping existing */}
                      <button
                        className="px-3 py-1 text-xs bg-white/10 text-white rounded-full hover:bg-white/20"
                        onClick={() => {
                          try {
                            if (
                              typeof window !== "undefined" &&
                              typeof window.exportChatHistory === "function"
                            ) {
                              window.exportChatHistory();
                            }
                          } catch (e) {
                            console.error("Error triggering chat export", e);
                          }
                        }}
                      >
                        Export
                      </button>
                    </>
                  )}
                  {participantApproveFunc &&
                    participantApproveFunc.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                          onClick={() => {
                            participantApproveFunc.forEach((item) =>
                              item.allow()
                            );
                            setTimeout(() => {
                              setParticipantApproveFunc([]);
                            }, 500);
                          }}
                        >
                          Admit All
                        </button>
                        <button
                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                          onClick={() => {
                            participantApproveFunc.forEach((item) =>
                              item.deny()
                            );
                            setTimeout(() => {
                              setParticipantApproveFunc([]);
                            }, 500);
                          }}
                        >
                          Deny All
                        </button>
                      </div>
                    )}
                  <button
                    className="text-white"
                    onClick={handleClose}
                    style={{ margin: 0, padding: 0 }}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            {sideBarMode === "PARTICIPANTS" ? (
              meetingSettings?.roomType === "Webinar" ? (
                <WebinarParticipantPanel panelHeight={panelHeight} />
              ) : (
                <ParticipantPanel panelHeight={panelHeight} />
              )
            ) : sideBarMode === "CHAT" ? (
              <>
                {/* Chat Summary Panel - shown above chat when active */}
                {showSummary && (
                  <div className="px-3 py-2 border-b border-white/10">
                    <ChatSummaryPanel
                      messages={[]} // Messages will be fetched inside the component
                      onClose={() => setShowSummary(false)}
                    />
                  </div>
                )}
                <AgoraRTMChatWrapper panelHeight={showSummary ? panelHeight - 150 : panelHeight} />
              </>
            ) : sideBarMode === "PROMPTER" ? (
              <PrompterPanel panelHeight={panelHeight} />
            ) : null}
          </>

          {/* AI Chat Assistant Modal */}
          <AIChatAssistant
            isOpen={showAIAssistant}
            onClose={() => setShowAIAssistant(false)}
            meetingContext={meetingSettings?.meetingName || "Meeting"}
          />

          {/* Notification Settings Modal */}
          <NotificationSettingsModal
            isOpen={showNotificationSettings}
            onClose={() => setShowNotificationSettings(false)}
            settings={notificationSettings}
            onSave={setNotificationSettings}
          />
        </div>
      </div>
    </div>
  );
};

export function SidebarConatiner({ height, sideBarContainerWidth }) {
  const { raisedHandsParticipants, sideBarMode, setSideBarMode } =
    useMeetingAppContext();
  const { prompterElement } = useSiteContext();
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const windowSize = useWindowSize();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobile && isLandscapeOrientation;

  const panelPadding = 8;
  const topBarHeight = 64;
  const bottomBarHeight = 60;

  const paddedHeight = height - panelPadding * 3.5;

  // Calculate mobile panel height:
  // Parent passes height as (viewportHeight - bottomBarHeight),
  // so on mobile we should only subtract the top bar here.
  const mobilePanelHeight = isMobile || isTab ? height - topBarHeight : height;

  // Track the on-screen keyboard's vertical occlusion so the mobile sidebar
  // panel (and the chat input inside it) lifts above the keyboard when it
  // opens. MUST be declared before `actualMobileHeight` below — that
  // calculation references `keyboardOffset` and a TDZ error fires on mobile
  // (only) if this useState is declared after its first use.
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  // Subtract `keyboardOffset` so the chat input area sits above the keyboard
  // rather than under it.  When the keyboard is closed the offset is 0 and
  // this matches the original calculation.
  const actualMobileHeight =
    isMobile || isTab
      ? (windowSize?.height || window.innerHeight) -
        topBarHeight -
        bottomBarHeight -
        keyboardOffset
      : height;

  const panelHeaderHeight = isMobile
    ? 40
    : isTab
    ? 44
    : isLGDesktop
    ? 48
    : isXLDesktop
    ? 52
    : 0;

  const panelHeaderPadding = isMobile
    ? 6
    : isTab
    ? 8
    : isLGDesktop
    ? 10
    : isXLDesktop
    ? 12
    : 0;

  const handleClose = () => {
    console.log("HANDLE CLOSE", sideBarMode);
    setSideBarMode(null);
  };

  // (keyboardOffset useState moved above — see comment near actualMobileHeight.)
  const isMobileOrTab = isMobile || isTab;
  useEffect(() => {
    if (!sideBarMode || !isMobileOrTab) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const occluded = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardOffset(occluded);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setKeyboardOffset(0);
    };
  }, [sideBarMode, isMobileOrTab]);

  return (
    <>
      {sideBarMode && (isTab || isMobile) && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{
            opacity: sideBarMode ? 1 : 0,
            pointerEvents: sideBarMode ? "auto" : "none",
            transition: "opacity 0.3s ease-out",
          }}
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={handleClose}
          />
          {/* Panel — `bottom` rides up with the keyboard so the chat input
              stays above it on mobile.  When no keyboard is open,
              keyboardOffset is 0 and `bottom` matches the bottom bar height. */}
          <div
            className="fixed left-0 right-0 bg-gray-800 shadow-xl transform"
            style={{
              top: `${topBarHeight}px`,
              bottom: `${bottomBarHeight + keyboardOffset}px`,
              transform: sideBarMode ? "translateY(0)" : "translateY(100%)",
              transition: "transform 0.3s ease-out",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SideBarTabView
              height={"100%"}
              sideBarContainerWidth={"100%"}
              panelHeight={Math.max(
                0,
                actualMobileHeight -
                  panelHeaderHeight -
                  panelHeaderPadding -
                  panelPadding * 2
              )}
              raisedHandsParticipants={raisedHandsParticipants}
              panelHeaderHeight={panelHeaderHeight}
              panelHeaderPadding={panelHeaderPadding}
              panelPadding={panelPadding}
              handleClose={handleClose}
            />
          </div>
        </div>
      )}
      {sideBarMode && !isMobile && !isTab ? (
        <SideBarTabView
          height={paddedHeight}
          sideBarContainerWidth={sideBarContainerWidth}
          panelHeight={paddedHeight - panelHeaderHeight - panelHeaderPadding}
          raisedHandsParticipants={raisedHandsParticipants}
          panelHeaderHeight={panelHeaderHeight}
          panelHeaderPadding={panelHeaderPadding}
          panelPadding={panelPadding}
          handleClose={handleClose}
        />
      ) : null}
    </>
  );
}
